<template>
  <div>
    <page-header
      :page="page"
      :actions="headerActionConfig"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />
    <ValidationObserver ref="formObserver" tag="form" novalidate>
      <general-information
        v-model="model.generalInformation"
        :activities="activities"
        :readOnly="readOnly"
        :students="model.students.assignedStudents"
        @clear="onClearStudents()"
      />
    </ValidationObserver>

    <student-leyend />

    <student-management :model.sync="model" :readOnly="readOnly" />
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import GeneralInformation from './GeneralInformation.vue';
import { getTransitionStudentActivities } from '@/modules/common/code.api';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import StudentManagement from '../students/StudentManagement';
import StudentLeyend from './StudentLeyend';
import ServiceTransitionStudent from '@/services/pei/transitionStudent.js';
import academicYearService from '@/services/academicyear';

const ROUTE_NAME_RESULTS = '/pei/transition-student';
const ROUTE_NAME_ADD_ACTIVITY = 'transition-student-12b';

export default {
  name: 'AddActivity',
  components: {
    PageHeader,
    GeneralInformation,
    StudentManagement,
    StudentLeyend,
  },
  mixins: [alertMixin],
  data: () => ({
    model: {
      generalInformation: {
        activity: null,
        date: null,
        description: null,
        type: null,
      },
      students: {
        available: [],
        filteredStudents: [],
        assignedStudents: [],
      },
    },
    isEditMode: false,
    canceled: false,
    activities: [],
  }),
  computed: {
    page() {
      return {
        icon: 'fas fa-user-edit',
        title:
          'SAEE-12b Informe para la recopilación de datos durante el proceso de transición',
        routes: [
          'Inicio',
          'PEI',
          'Perfil del estudiante en proceso de transición escolar',
        ],
      };
    },
    students() {
      return (this.model.students && this.model.students.data) || [];
    },
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'success',
          visible: true,
          click: () => this.$router.push(ROUTE_NAME_RESULTS),
        },
        {
          text: 'Cancelar',
          icon: 'fas fa-times-circle',
          variant: 'outline-danger',
          visible: this.isEditMode,
          click: () => this.cancel(),
        },
        {
          text: 'Someter',
          icon: 'fas fa-flag',
          variant: 'success',
          visible: !this.readOnly,
          click: () => this.save(),
        },
        {
          text: 'Editar',
          icon: 'fas fa-edit',
          variant: 'success',
          visible: this.readOnly & !this.isEditMode,
          click: () => this.onEditMode(),
        },
      ];
    },
    isIndividual() {
      return this.model.generalInformation.type?.id == 1; //Individual
    },
    academicYearId() {
      const yearId = this.$router.currentRoute.params.academicYearId;
      return parseInt(yearId);
    },
    transitionStudenId() {
      const id = this.$router.currentRoute.params.id;
      return parseInt(id) || null;
    },
    readOnly() {
      return Boolean(this.transitionStudenId) && !this.isEditMode;
    },
  },
  async mounted() {
    await this.getActivities();
    await this.getDetail();
  },
  methods: {
    async getActivities() {
      const { data } = await getTransitionStudentActivities();
      this.activities = data;
    },
    onEditMode(edit = true) {
      this.isEditMode = edit;
    },
    async cancel() {
      this.onEditMode(false);
      await this.getDetail();
    },
    async save() {
      const isValid = await this.$refs.formObserver.validate();
      if (!isValid) {
        this.ShowToast(
          'Error',
          `Favor de completar la informacion solicitada`,
          'error'
        );
        return;
      }

      const listOfSie = this.model.students.assignedStudents?.map(
        (x) => x.studentSieId
      );

      if (listOfSie.length == 0) {
        this.ShowToast(
          'Error',
          `Favor de seleccionar al menos un estudiante`,
          'error'
        );
        return;
      }

      const payload = {
        id: this.transitionStudenId,
        academicYearId: this.academicYearId,
        activityId: this.model.generalInformation?.activity?.id,
        date: this.model.generalInformation?.date,
        description: this.model.generalInformation?.description,
        isIndividual: this.isIndividual,
        students: this.model.students.assignedStudents?.map(
          (x) => x.studentSieId
        ),
      };

      const { data } = await ServiceTransitionStudent.submitTransitionStudent(
        payload
      );
      if (data) {
        this.ShowToast(
          'Exito',
          `La informacion se ha guardado correctamente`,
          'success'
        );
        if (this.isEditMode) {
          this.isEditMode = false;
          return;
        }

        this.$router.push({
          name: ROUTE_NAME_ADD_ACTIVITY,
          params: {
            academicYearId: this.academicYearId,
            id: data,
          },
        });
      }
    },
    async getDetail() {
      if (!this.transitionStudenId) return;
      const { data } = await ServiceTransitionStudent.getDetail12b(
        this.transitionStudenId
      );
      const {
        data: academicYear,
      } = await academicYearService.getAcademicYearForDate(data.date);

      this.model.generalInformation.activity = this.activities.find(
        (x) => x.id == data.activityId
      );
      this.model.generalInformation.date = data.date;
      this.model.generalInformation.description = data.description;
      this.model.generalInformation.type = {
        id: data.isIndividual ? 1 : 2,
        name: data.isIndividual ? 'Individual' : 'Grupal',
      };

      this.model.students.assignedStudents = data.students.map((x) => ({
        academicYearId: academicYear.id,
        studentSieId: x.studentId,
        fullName: x.fullName,
        age: x.age,
        impediment: {
          name: x.impediment,
        },
        status: {
          name: x.status,
        },
        grade: {
          name: x.grade,
        },
      }));
    },
    onClearStudents() {
      this.model.students.assignedStudents = [];
    },
  },
};
</script>

<style lang="scss" scoped></style>
